import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Flame, Clock, AlertTriangle, CheckCircle, 
  Briefcase, TrendingUp, Calendar, RefreshCw, Search,
  Eye, Filter, ChevronLeft, ChevronRight, X, Mail, Phone
} from 'lucide-react';
import BASE_URL from '../../url';
import CandidateDetail from '../../Resume_Submission/Components/CandidateDetail';

const PRIORITY_BADGES = {
  CRITICAL: { bg: '#fee2e2', color: '#991b1b', label: '🔥 Critical' },
  HIGH: { bg: '#fee2e2', color: '#b91c1c', label: '🔴 High' },
  MEDIUM: { bg: '#fef3c7', color: '#92400e', label: '🟡 Medium' },
  LOW: { bg: '#d1fae5', color: '#065f46', label: '🟢 Low' },
};

const getAgingInfo = (days) => {
  if (days >= 46) {
    return { label: 'Critical', bg: '#fee2e2', color: '#991b1b', icon: '🔴' };
  } else if (days >= 31) {
    return { label: 'Hot', bg: '#ffedd5', color: '#c2410c', icon: '🟠' };
  } else if (days >= 16) {
    return { label: 'Warming', bg: '#fef3c7', color: '#92400e', icon: '🟡' };
  } else {
    return { label: 'Fresh', bg: '#d1fae5', color: '#065f46', icon: '🟢' };
  }
};

const normalizeWorkAuth = (auth) => {
  if (!auth) return 'Other';
  const a = auth.trim();
  const lower = a.toLowerCase();
  if (lower === 'h1b' || lower === 'h1-b' || lower === 'h1-b visa' || lower === 'h-1b') return 'H1B';
  if (lower === 'gc' || lower === 'green card') return 'Green Card';
  if (lower === 'citizen' || lower === 'us citizen' || lower === 'usc' || lower === 'us citizenship') return 'US Citizen';
  if (lower === 'opt' || lower === 'cpt' || lower === 'opt/cpt' || lower === 'opt-cpt' || lower === 'opt / cpt') return 'OPT/CPT';
  if (lower === 'h4' || lower === 'h4 ead' || lower === 'h4-ead') return 'H4 EAD';
  if (lower === 'l2' || lower === 'l2 ead' || lower === 'l2-ead') return 'L2 EAD';
  if (lower === 'tn' || lower === 'tn visa' || lower === 'tn-1') return 'TN Visa';
  if (lower === 'unknown' || lower === 'n/a' || lower === 'none' || lower === 'null') return 'Other';
  return a;
};

const toYMD = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const truncateText = (text, maxLength = 20) => {
  if (!text || text === '—') return text || '—';
  const str = String(text).trim();
  if (str.length <= maxLength) return str;
  return `${str.substring(0, maxLength)}...`;
};

const formatSubmissionDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
};

const getSubmissionDaysAgo = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const diffDays = Math.max(0, Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)));
  return diffDays;
};

const getSubmissionRelativeText = (d) => {
  const days = getSubmissionDaysAgo(d);
  if (days === null) return null;
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

export const BenchDashboard = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [benchRecruiterList, setBenchRecruiterList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // Date Filters
  const [quickSelect, setQuickSelect] = useState('All Time');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Days on Bench (Aging) Range Filter
  const [daysRange, setDaysRange] = useState('all'); // 'all', '1-50', '50-100', '100-200', '200+'
  const [customDays, setCustomDays] = useState(''); // exact numeric days input (e.g. 2, 7, 22)

  // Table Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');

  // Pagination with sessionStorage
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem('bench_dashboard_currentPage');
    return saved !== null ? parseInt(saved, 10) : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = sessionStorage.getItem('bench_dashboard_pageSize');
    return saved !== null ? parseInt(saved, 10) : 30;
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
    sessionStorage.setItem('bench_dashboard_currentPage', page);
  };

  const handlePageSizeChange = (size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
    sessionStorage.setItem('bench_dashboard_pageSize', size);
    sessionStorage.setItem('bench_dashboard_currentPage', 1);
  };

  // Fetch bench recruiters for dropdown
  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/bench-manage/recruiters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success && Array.isArray(res.data.recruiters)) {
          setBenchRecruiterList(res.data.recruiters);
        }
      } catch (err) {
        console.error('Error fetching bench recruiters:', err);
      }
    };
    fetchRecruiters();
  }, []);

  // Fetch all bench candidates using dedicated fast Bench API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/bench-manage/candidates?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const rawData = res.data?.data || res.data?.candidates || (Array.isArray(res.data) ? res.data : []);
      if (Array.isArray(rawData)) {
        // Transform items directly from bench API
        const transformed = rawData.map(c => {
          const createdDt = c.CreatedAtUtc || c.created_on || c.CreatedDate;
          const outOfJobSince = c.OutOfJobSince || createdDt;
          
          let daysOnBench = c.DaysOnBench;
          if (daysOnBench === undefined || daysOnBench === null) {
            daysOnBench = 0;
            if (outOfJobSince) {
              const d = new Date(outOfJobSince);
              if (!isNaN(d.getTime())) {
                daysOnBench = Math.max(0, Math.floor((new Date() - d) / (1000 * 60 * 60 * 24)));
              }
            }
          }

          let daysUntilExpiry = c.DaysUntilExpiry;
          if (daysUntilExpiry === undefined && c.WorkAuthExpiryDate) {
            const expDate = new Date(c.WorkAuthExpiryDate);
            if (!isNaN(expDate.getTime())) {
              daysUntilExpiry = Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24));
            }
          }

          const rawAssignees = Array.isArray(c.BenchAssignees) 
            ? c.BenchAssignees 
            : (c.PrimaryAssignee || c.BenchAssignee ? [c.PrimaryAssignee || c.BenchAssignee] : []);

          return {
            CandidateId: c.CandidateId || c.candidate_id,
            CandidateCode: c.CandidateCode || c.candidate_code || `CAN-${c.CandidateId || c.candidate_id}`,
            FirstName: c.FirstName || c.first_name || '',
            LastName: c.LastName || c.last_name || '',
            Email: c.Email || c.email || '',
            Phone: c.Phone || c.phone || '',
            JobTitle: c.JobTitle || c.job_title || '',
            CandidateStatus: c.CandidateStatus || c.status || 'Available',
            CreatedDate: createdDt,
            CreatedBy: c.PrimaryAssignee || c.BenchAssignee || 'Admin',
            WorkAuthorization: normalizeWorkAuth(c.WorkAuthorization || c.work_authorization),
            WorkAuthPetitioner: c.WorkAuthPetitioner || '',
            WorkAuthExpiryDate: c.WorkAuthExpiryDate || '',
            DaysUntilExpiry: daysUntilExpiry,
            PriorityCode: (c.PriorityCode || '').toUpperCase(),
            OutOfJobSince: outOfJobSince,
            DaysOnBench: daysOnBench,
            NewJobDeadline: c.NewJobDeadline || null,
            RecentPlacementDate: c.LatestJobSubmissionDate || c.RecentPlacementDate || null,
            LatestJobSubmissionDate: c.LatestJobSubmissionDate || null,
            DaysSinceLastSubmission: c.DaysSinceLastSubmission,
            LastSubmissionRelative: c.LastSubmissionRelative,
            LastSubmissionDateFormatted: c.LastSubmissionDateFormatted,
            BenchAssignee: c.PrimaryAssignee || c.BenchAssignee || '',
            BenchAssignees: rawAssignees,
            Skills: Array.isArray(c.Skills) ? c.Skills : []
          };
        });

        setCandidates(transformed);
      }
    } catch (e) {
      console.error('Error fetching bench dashboard data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedCandidateId) {
        setSelectedCandidateId(null);
        fetchData();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCandidateId, fetchData]);

  // Handle Quick Select changes
  const handleQuickSelectChange = (e) => {
    const val = e.target.value;
    setQuickSelect(val);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let start = null;
    let end = null;

    if (val === 'Today') {
      start = today;
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
    } else if (val === 'Yesterday') {
      start = new Date(today);
      start.setDate(start.getDate() - 1);
      end = new Date(start);
      end.setHours(23, 59, 59, 999);
    } else if (val === 'This Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(today.setDate(diff));
      end = new Date();
    } else if (val === 'Last Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.setDate(diff));
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (val === 'This Month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date();
    } else if (val === 'Last Month') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    } else if (val === 'This Quarter') {
      const quarter = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), quarter * 3, 1);
      end = new Date();
    } else if (val === 'Last Quarter') {
      const quarter = Math.floor(now.getMonth() / 3) - 1;
      start = new Date(now.getFullYear(), quarter * 3, 1);
      end = new Date(now.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999);
    } else if (val === 'All Time') {
      start = null;
      end = null;
    }

    setStartDate(start ? toYMD(start) : '');
    setEndDate(end ? toYMD(end) : '');
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setQuickSelect('All Time');
    setStartDate('');
    setEndDate('');
    setDaysRange('all');
    setCustomDays('');
    setSearchTerm('');
    setPriorityFilter('all');
    setAssigneeFilter('all');
    setCurrentPage(1);
  };

const DEFAULT_BENCH_RECRUITERS = [
  'Abinesh Anbazhagan',
  'Karunakaran Palani',
  'Pragadeeshwaran karthikeyan',
  'Rohinth Chelladurai'
];

  // Distinct Recruiter Options (from default list + backend recruiters + any assignee in candidates)
  const allRecruiterOptions = useMemo(() => {
    const fromApi = (benchRecruiterList || []).map(r => r.name || r.UserName).filter(Boolean);
    const fromCandidates = (candidates || []).flatMap(c => (c.BenchAssignees && c.BenchAssignees.length > 0 ? c.BenchAssignees : [c.BenchAssignee])).filter(Boolean);
    return [...new Set([...DEFAULT_BENCH_RECRUITERS, ...fromApi, ...fromCandidates])].sort();
  }, [benchRecruiterList, candidates]);

  // Master Filter Pipeline
  const filteredCandidates = useMemo(() => {
    return candidates.filter(c => {
      // 1. Date Range Filter (against CreatedDate / parsed date)
      if (startDate) {
        const cDate = new Date(c.CreatedDate);
        const sDate = new Date(startDate);
        sDate.setHours(0, 0, 0, 0);
        if (cDate < sDate) return false;
      }
      if (endDate) {
        const cDate = new Date(c.CreatedDate);
        const eDate = new Date(endDate);
        eDate.setHours(23, 59, 59, 999);
        if (cDate > eDate) return false;
      }

      // 2a. Days on Bench Dropdown Range Filter
      if (daysRange === '1-50') {
        if (c.DaysOnBench < 1 || c.DaysOnBench > 50) return false;
      } else if (daysRange === '50-100') {
        if (c.DaysOnBench < 50 || c.DaysOnBench > 100) return false;
      } else if (daysRange === '100-200') {
        if (c.DaysOnBench < 100 || c.DaysOnBench > 200) return false;
      } else if (daysRange === '200+') {
        if (c.DaysOnBench < 200) return false;
      }

      // 2b. Exact Days on Bench Filter (Numeric input e.g. 2, 7, 22)
      if (customDays !== '' && !isNaN(Number(customDays))) {
        const targetDays = parseInt(customDays, 10);
        if (c.DaysOnBench !== targetDays) return false;
      }

      // 3. Priority Filter
      if (priorityFilter !== 'all') {
        if (c.PriorityCode !== priorityFilter.toUpperCase()) return false;
      }

      // 4. Bench Recruiter Filter (checks across all assigned recruiters)
      if (assigneeFilter !== 'all') {
        const hasMatch = (c.BenchAssignees && c.BenchAssignees.length > 0)
          ? c.BenchAssignees.some(a => (a || '').toLowerCase() === assigneeFilter.toLowerCase())
          : (c.BenchAssignee || '').toLowerCase() === assigneeFilter.toLowerCase();
        if (!hasMatch) return false;
      }

      // 5. Search Term (Name, Code, Title, Email, Phone, Skills, Petitioner)
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        const fullName = `${c.FirstName} ${c.LastName}`.toLowerCase();
        const code = (c.CandidateCode || '').toLowerCase();
        const email = (c.Email || '').toLowerCase();
        const phone = (c.Phone || '').toLowerCase();
        const title = (c.JobTitle || '').toLowerCase();
        const pet = (c.WorkAuthPetitioner || '').toLowerCase();
        const skills = (c.Skills || []).join(' ').toLowerCase();

        const match = fullName.includes(term) || code.includes(term) || email.includes(term) || 
                      phone.includes(term) || title.includes(term) || pet.includes(term) || skills.includes(term);
        if (!match) return false;
      }

      return true;
    });
  }, [candidates, startDate, endDate, daysRange, customDays, priorityFilter, assigneeFilter, searchTerm]);

  // Dynamic Dashboard Stats computed from the active filtered candidates
  const stats = useMemo(() => {
    const total = filteredCandidates.length;
    const available = filteredCandidates.filter(c => c.CandidateStatus === 'Available').length;
    const inProcess = filteredCandidates.filter(c => c.CandidateStatus === 'In Process').length;
    const placed = filteredCandidates.filter(c => c.CandidateStatus === 'Hired' || c.CandidateStatus === 'Placed').length;

    // Aging counts
    let fresh = 0, warming = 0, hot = 0, critical = 0;
    filteredCandidates.forEach(c => {
      if (c.DaysOnBench >= 46) critical++;
      else if (c.DaysOnBench >= 31) hot++;
      else if (c.DaysOnBench >= 16) warming++;
      else fresh++;
    });

    // Work auth counts
    const workAuthMap = {};
    filteredCandidates.forEach(c => {
      const wa = c.WorkAuthorization || 'Other';
      workAuthMap[wa] = (workAuthMap[wa] || 0) + 1;
    });

    // Visas expiring within 60 days
    const expiring = filteredCandidates.filter(c => c.DaysUntilExpiry !== null && c.DaysUntilExpiry >= 0 && c.DaysUntilExpiry <= 60)
      .sort((a, b) => a.DaysUntilExpiry - b.DaysUntilExpiry);

    return {
      summary: { TotalBench: total, Available: available, InProcess: inProcess, Placed: placed },
      aging: { FreshCount: fresh, WarmingCount: warming, HotCount: hot, CriticalCount: critical },
      workAuthMix: Object.entries(workAuthMap).map(([WorkAuth, CandidateCount]) => ({ WorkAuth, CandidateCount })),
      expiringVisas: expiring
    };
  }, [filteredCandidates]);

  // Paginated candidates for table
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage) || 1;
  const paginatedCandidates = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCandidates.slice(start, start + itemsPerPage);
  }, [filteredCandidates, currentPage, itemsPerPage]);

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto', fontFamily: 'inherit' }}>
      
      {/* ── Top Header ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Briefcase size={26} color="#3b5bfd" />
            Bench Management
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
            Live marketing pipeline, aging tracking & consultant allocation
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={fetchData}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db',
              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Date Filters & Days on Bench Filter Bar ────────────────── */}
      <div style={{
        background: '#fff', borderRadius: '8px', padding: '14px 18px',
        marginBottom: '24px', border: '1px solid #e2e8f0',
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Date filters:</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
            
            {/* Quick Select */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Quick Select</label>
              <select
                value={quickSelect}
                onChange={handleQuickSelectChange}
                style={{
                  padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1',
                  outline: 'none', fontSize: '13px', minWidth: '130px', color: '#334155', background: '#fff'
                }}
              >
                <option value="All Time">All Time</option>
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="This Week">This Week</option>
                <option value="Last Week">Last Week</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
                <option value="This Quarter">This Quarter</option>
                <option value="Last Quarter">Last Quarter</option>
              </select>
            </div>

            {/* Start Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setQuickSelect('Custom'); setCurrentPage(1); }}
                style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', color: '#334155' }}
              />
            </div>

            {/* End Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>End date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setQuickSelect('Custom'); setCurrentPage(1); }}
                style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', color: '#334155' }}
              />
            </div>

            {/* Loading Indicator / Status */}
            <div style={{ display: 'flex', alignItems: 'center', height: '35px', padding: '0 8px' }}>
              {loading && <span style={{ fontSize: '12px', color: '#3b5bfd', fontWeight: 600 }}>Loading...</span>}
            </div>

            {/* Days on Bench Filter (Moved to Loading right side) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Days on Bench</label>
              <select
                value={daysRange}
                onChange={(e) => { setDaysRange(e.target.value); setCurrentPage(1); }}
                style={{
                  padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1',
                  outline: 'none', fontSize: '13px', minWidth: '150px', color: '#334155', background: '#fff'
                }}
              >
                <option value="all">All Days</option>
                <option value="1-50">1 - 50 days (Fresh / Warm)</option>
                <option value="50-100">50 - 100 days (Aging)</option>
                <option value="100-200">100 - 200 days (Long)</option>
                <option value="200+">200+ days (Critical)</option>
              </select>
            </div>

            {/* Custom Numeric Days Input (Right Side) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Days (e.g. 7)</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="e.g. 7"
                  value={customDays}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setCustomDays(val);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '7px 26px 7px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    outline: 'none',
                    fontSize: '13px',
                    width: '80px',
                    color: '#334155',
                    background: '#fff'
                  }}
                />
                {customDays && (
                  <X
                    size={13}
                    onClick={() => { setCustomDays(''); setCurrentPage(1); }}
                    style={{
                      position: 'absolute',
                      right: '7px',
                      cursor: 'pointer',
                      color: '#94a3b8'
                    }}
                  />
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Clear Filters Button */}
        <div>
          {(quickSelect !== 'All Time' || startDate || endDate || daysRange !== 'all' || customDays || searchTerm || priorityFilter !== 'all' || assigneeFilter !== 'all') && (
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
                background: '#f8fafc', color: '#64748b', cursor: 'pointer', fontSize: '13px',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <X size={14} /> Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ── 4 Top KPI Cards ────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827' }}>{stats.summary.TotalBench}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Active bench consultants</div>
        </div>

        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#2563eb' }}>{stats.summary.Available}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Ready for marketing</div>
        </div>

        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#b45309' }}>{stats.summary.InProcess}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>In active client interviews</div>
        </div>

        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#4338ca' }}>{stats.summary.Placed}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Deployed on billing project</div>
        </div>

      </div>

      {/* ── Embedded Bench Candidates Table Section ───────────────── */}
      <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        
        {/* Table Filter Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={17} color="#3b5bfd" />
              Bench Consultants ({filteredCandidates.length} records found)
            </h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', alignItems: 'center' }}>
            {/* Search Input */}
            <div style={{ position: 'relative', gridColumn: 'span 2' }}>
              <Search size={15} color="#9ca3af" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Search by name, title, code, email, phone, petitioner..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                style={{
                  width: '100%', padding: '7px 10px 7px 32px', borderRadius: '6px',
                  border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Priority Filter */}
            <div>
              <select
                value={priorityFilter}
                onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}
                style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
              >
                <option value="all">All Priorities</option>
                <option value="CRITICAL">🔥 Critical Priority</option>
                <option value="HIGH">🔴 High Priority</option>
                <option value="MEDIUM">🟡 Medium Priority</option>
                <option value="LOW">🟢 Low Priority</option>
              </select>
            </div>

            {/* Bench Recruiter (Assignee) Filter */}
            <div>
              <select
                value={assigneeFilter}
                onChange={(e) => { setAssigneeFilter(e.target.value); setCurrentPage(1); }}
                style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
              >
                <option value="all">All Bench Recruiters</option>
                {allRecruiterOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12.5px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                <th style={{ padding: '9px 12px' }}>Consultant</th>
                <th style={{ padding: '9px 12px' }}>Email</th>
                <th style={{ padding: '9px 12px' }}>Phone</th>
                <th style={{ padding: '9px 12px' }}>Job Title</th>
                <th style={{ padding: '9px 12px' }}>Bench Priority</th>
                <th style={{ padding: '9px 12px' }}>Days on Bench</th>
                <th style={{ padding: '9px 12px' }}>Bench Recruiter</th>
                <th style={{ padding: '9px 12px' }}>Work Auth & Petitioner</th>
                <th style={{ padding: '9px 12px' }}>Placement Deadline</th>
                <th style={{ padding: '9px 12px' }}>Last Submission</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ padding: '30px', textAlign: 'center', color: '#6b7280' }}>
                    Loading bench consultants...
                  </td>
                </tr>
              ) : paginatedCandidates.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: '30px', textAlign: 'center', color: '#6b7280' }}>
                    No bench candidates found matching the selected filters.
                  </td>
                </tr>
              ) : (
                paginatedCandidates.map((c) => {
                  const pBadge = PRIORITY_BADGES[c.PriorityCode] || { bg: '#f3f4f6', color: '#6b7280', label: '—' };
                  const aging = getAgingInfo(c.DaysOnBench);

                  return (
                    <tr 
                      key={c.CandidateId}
                      onClick={() => setSelectedCandidateId(c.CandidateId)}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      title="Click to view candidate overview popup"
                    >
                      {/* Consultant */}
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600, color: '#111827' }} title={`${c.FirstName} ${c.LastName}`}>
                          {truncateText(`${c.FirstName} ${c.LastName}`, 20)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{c.CandidateCode}</div>
                      </td>

                      {/* Email */}
                      <td style={{ padding: '8px 12px', color: '#1e293b', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.Email}>
                        {c.Email ? (
                          <a href={`mailto:${c.Email}`} onClick={(e) => e.stopPropagation()} style={{ color: '#3b5bfd', textDecoration: 'none' }} title={c.Email}>
                            {truncateText(c.Email, 20)}
                          </a>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>

                      {/* Phone */}
                      <td style={{ padding: '8px 12px', color: '#334155', whiteSpace: 'nowrap' }}>
                        {c.Phone ? (
                          <a href={`tel:${c.Phone}`} onClick={(e) => e.stopPropagation()} style={{ color: '#334155', textDecoration: 'none' }}>
                            {c.Phone}
                          </a>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>

                      {/* Job Title */}
                      <td style={{ padding: '8px 12px', color: '#334155', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.JobTitle}>
                        {truncateText(c.JobTitle, 20)}
                      </td>

                      {/* Bench Priority */}
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          padding: '2px 7px', borderRadius: '5px', fontSize: '11px', fontWeight: 600,
                          background: pBadge.bg, color: pBadge.color, lineHeight: 1.3, display: 'inline-block'
                        }}>
                          {pBadge.label}
                        </span>
                      </td>

                      {/* Days on Bench */}
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          padding: '2px 7px', borderRadius: '5px', fontSize: '11px', fontWeight: 600,
                          background: aging.bg, color: aging.color, display: 'inline-flex', alignItems: 'center', gap: '3px',
                          lineHeight: 1.3
                        }}>
                          <Flame size={11} /> {c.DaysOnBench} days ({aging.label})
                        </span>
                      </td>

                      {/* Bench Recruiter */}
                      <td style={{ padding: '8px 12px', color: '#111827', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {(() => {
                          const list = (c.BenchAssignees && c.BenchAssignees.length > 0)
                            ? c.BenchAssignees
                            : (c.BenchAssignee ? [c.BenchAssignee] : []);
                          if (list.length === 0) return <span style={{ color: '#9ca3af' }}>Unassigned</span>;

                          const primary = list.find(r => typeof r === 'object' && (r.IsPrimary === 1 || r.IsPrimary === true)) || list[0];
                          const primaryName = typeof primary === 'string' ? primary : (primary.UserName || primary.name || '');
                          const remaining = list.filter(r => (typeof r === 'string' ? r : (r.UserName || r.name || '')) !== primaryName);
                          const displayName = primaryName.length > 15 ? `${primaryName.substring(0, 15)}...` : primaryName;
                          const allNames = list.map(r => typeof r === 'string' ? r : (r.UserName || r.name || '')).filter(Boolean).join(', ');

                          return (
                            <div 
                              title={allNames}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', cursor: 'default' }}
                            >
                              <span
                                title={allNames}
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #bfdbfe',
                                  padding: '2px 6px',
                                  borderRadius: '5px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  cursor: 'default',
                                  lineHeight: 1.3
                                }}
                              >
                                <span style={{ fontSize: '10px' }}>⭐</span>
                                <span>{displayName}</span>
                              </span>

                              {remaining.length > 0 && (
                                <span
                                  title={allNames}
                                  style={{
                                    fontSize: '10.5px',
                                    fontWeight: 700,
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    padding: '1px 5px',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    lineHeight: 1.3
                                  }}
                                >
                                  +{remaining.length}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Work Auth */}
                      <td style={{ padding: '8px 12px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 500, color: '#334155' }}>{c.WorkAuthorization || '—'}</div>
                        {c.WorkAuthPetitioner && (
                          <div style={{ fontSize: '11px', color: '#64748b' }} title={c.WorkAuthPetitioner}>
                            Petitioner: {truncateText(c.WorkAuthPetitioner, 15)}
                          </div>
                        )}
                      </td>

                      {/* Placement Deadline */}
                      <td style={{ padding: '8px 12px', color: '#475569', whiteSpace: 'nowrap' }}>
                        {c.NewJobDeadline ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                            <Calendar size={12} color="#3b5bfd" />
                            {new Date(c.NewJobDeadline).toLocaleDateString()}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>

                      {/* Last Submission (Matching Image exactly) */}
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {c.LatestJobSubmissionDate ? (() => {
                          const days = getSubmissionDaysAgo(c.LatestJobSubmissionDate);
                          const dateStr = formatSubmissionDate(c.LatestJobSubmissionDate);
                          const relStr = getSubmissionRelativeText(c.LatestJobSubmissionDate);
                          const isOld = days !== null && days >= 20;
                          const relColor = isOld ? '#dc2626' : '#3b5bfd';

                          return (
                            <div style={{ lineHeight: '1.3' }}>
                              <div style={{ fontWeight: 600, fontSize: '12px', color: '#1e293b' }}>
                                {dateStr}
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: 600, color: relColor, marginTop: '2px' }}>
                                {relStr}
                              </div>
                            </div>
                          );
                        })() : (
                          <span style={{ color: '#9ca3af' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#64748b', background: '#fff',
          flexWrap: 'wrap', gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>
              Showing <strong style={{ color: '#1e293b' }}>{filteredCandidates.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}–{Math.min(currentPage * itemsPerPage, filteredCandidates.length)}</strong> of <strong style={{ color: '#1e293b' }}>{filteredCandidates.length}</strong>
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              style={{
                padding: '3px 8px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '12px',
                color: '#334155',
                background: '#fff',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              disabled={currentPage <= 1}
              onClick={() => handlePageChange(currentPage - 1)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                background: '#fff',
                color: currentPage <= 1 ? '#cbd5e1' : '#64748b',
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 500
              }}
            >
              <ChevronLeft size={14} /> Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .map((p, idx, arr) => {
                const prev = arr[idx - 1];
                return (
                  <React.Fragment key={p}>
                    {prev && p - prev > 1 && (
                      <span style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                    )}
                    <button
                      onClick={() => handlePageChange(p)}
                      style={{
                        minWidth: '28px',
                        height: '28px',
                        padding: '0 6px',
                        borderRadius: '6px',
                        border: p === currentPage ? 'none' : '1px solid #e2e8f0',
                        background: p === currentPage ? '#3b5bfd' : '#fff',
                        color: p === currentPage ? '#fff' : '#64748b',
                        fontWeight: p === currentPage ? 700 : 500,
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}

            <button
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                background: '#fff',
                color: currentPage >= totalPages ? '#cbd5e1' : '#64748b',
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 500
              }}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>

      </div>

      {/* ── Candidate Detail / Overview Modal Popup ── */}
      {selectedCandidateId && (
        <CandidateDetail
          candidateId={selectedCandidateId}
          isModal={true}
          initialTab="overview"
          onClose={() => {
            setSelectedCandidateId(null);
            fetchData();
          }}
        />
      )}

    </div>
  );
};

export default BenchDashboard;
